import * as React from 'react';
import * as styles from './create-segment.module.scss';
import { PrimaryButton, SecondaryAlert, TertiaryButton } from 'kagr-ui';
import Paths from '../../models/enums/paths';
import { useHistory, useLocation } from 'react-router-dom';
import { CollapsibleSection } from '../common/collapsible-section';
import {
  SegmentAttributeOperator,
  SegmentCompositionSelection,
  SegmentDetailResponseParsed,
  SegmentResults
} from '../../models/segments';
import { useSegmentUpdate } from '../../hooks/useSegmentUpdate';
import ViewSegmentDetails from './view-segment/view-segment-details';
import ViewSegmentAttributes from './view-segment/view-segment-attributes';
import SegmentResultsView from './segment-results/segment-results-view';
import { UseQueryResult, useQueryClient } from '@tanstack/react-query';
import EditSegmentDetails from './build-segment/edit-segment-details';
import ExportSegment from './export-segment/export-segment';
import {
  BuildSegmentContext,
  BuildSegmentExcludedContext,
  buildSegmentReducer,
  BuildExcludeSelectedCompositionContext,
  BuildIncludeSelectedCompositionContext
} from './build-segment/useBuildSegment';
import SegmentResultsBuild from './segment-results/segment-results-build';
import BuildSegment from './build-segment/build-segment';
import { datadogRum } from '@datadog/browser-rum';
import { withStyles } from '@material-ui/core';

type SegmentProps = {
  segmentId: string;
  segmentQuery: UseQueryResult<SegmentDetailResponseParsed, Error>;
  powerUser: boolean;
  userName: string;
};

const DependencyAlert = withStyles({
  root: {
    width: '55%'
  }
})(SecondaryAlert);

/**
 * View or update a single segment
 * Component renders either a view-only or editable segment view conditionally based on the ownership of the segment.
 */
const Segment = ({ segmentId, segmentQuery, powerUser, userName }: SegmentProps) => {
  const history = useHistory();
  const segmentMutation = useSegmentUpdate(segmentId);
  const { state } = useLocation<{ isEditing?: boolean }>();
  const [isEditingSegment, setIsEditingSegment] = React.useState<boolean>(state?.isEditing);
  const [detailsCollapsed, setDetailsCollapsed] = React.useState<boolean>(false);
  const [buildCollapsed, setBuildCollapsed] = React.useState<boolean>(false);
  const [name, setName] = React.useState<string>('');
  const [description, setDescription] = React.useState<string>('');
  const [shared, setShared] = React.useState<string>('');
  const [includedAttributes, dispatch] = React.useReducer(buildSegmentReducer, {
    operator: SegmentAttributeOperator.And,
    predicates: []
  });
  const [excludedAttributes, excludedDispatch] = React.useReducer(buildSegmentReducer, {
    operator: SegmentAttributeOperator.And,
    predicates: []
  });
  const [canEditSegment, setCanEditSegment] = React.useState<boolean>(false);
  const [isDependency, setIsDependency] = React.useState<boolean>(false);
  const dependencyMessage = `Changes made to this segment will impact dependent segments. For more details see "Dependencies" tab.`;

  const [includeSegmentComposition, setIncludeSegmentComposition] = React.useState<SegmentCompositionSelection>({
    segmentId: null,
    segmentOperator: null,
    customers: null,
    segmentName: ''
  });
  const [excludeSegmentComposition, setExcludeSegmentComposition] = React.useState<SegmentCompositionSelection>({
    segmentId: null,
    segmentOperator: null,
    customers: null,
    segmentName: ''
  });

  const queryClient = useQueryClient();

  function updateSegment() {
    let selectedComposition = {};

    if (includeSegmentComposition.segmentId) {
      selectedComposition = { include: includeSegmentComposition };
    }
    if (excludeSegmentComposition.segmentId) {
      selectedComposition = { ...selectedComposition, exclude: excludeSegmentComposition };
    }

    segmentMutation.mutate(
      {
        name,
        description,
        isShared: shared == 'Share',
        attributes: {
          include: includedAttributes,
          exclude: excludedAttributes
        },
        composition: {
          ...selectedComposition
        }
      },
      {
        onSuccess: () => {
          includedAttributes.predicates.forEach((att) => {
            logCreatedAttributes(att);
          });
          excludedAttributes.predicates.forEach((att) => {
            logCreatedAttributes(att);
          });
          history.push(Paths.Segments, { successMessage: `Success: Segment "${name}" Saved` });
        }
      }
    );
  }

  const logCreatedAttributes = (included: any) => {
    if ('predicates' in included) {
      included.predicates.forEach((att) => {
        logCreatedAttributes(att);
      });
    } else {
      datadogRum.addUserAction('edit-segment', {
        user_action_details: {
          segment_id: segmentId,
          segment_name: name,
          is_segment_shared: shared,
          user_name: userName,
          attribute: included.name
        }
      });
    }
  };

  React.useEffect(() => {
    if (segmentQuery.data) {
      setName(segmentQuery.data.name);
      setDescription(segmentQuery.data.description);
      setShared(segmentQuery.data.isShared ? 'Share' : 'Do not share');
      dispatch({ type: 'setAttributes', attributes: segmentQuery.data.selectedAttributes.include });
      excludedDispatch({ type: 'setAttributes', attributes: segmentQuery.data.selectedAttributes.exclude });
      queryClient.setQueryData<SegmentResults>(
        [
          'segment-results',
          segmentQuery.data.clientId,
          segmentQuery.data.selectedAttributes.include,
          segmentQuery.data.selectedAttributes.exclude
        ],
        segmentQuery.data.results
      );
      if (segmentQuery.data.composition?.include) {
        setIncludeSegmentComposition({
          segmentId: segmentQuery.data.composition.include.segmentId,
          segmentOperator: segmentQuery.data.composition.include.segmentOperator,
          customers: segmentQuery.data.composition.include.customers,
          segmentName: segmentQuery.data.composition.include.segmentName
        });
      }
      if (segmentQuery.data.composition?.exclude) {
        setExcludeSegmentComposition({
          segmentId: segmentQuery.data.composition.exclude.segmentId,
          segmentOperator: segmentQuery.data.composition.exclude.segmentOperator,
          customers: segmentQuery.data.composition.exclude.customers,
          segmentName: segmentQuery.data.composition.exclude.segmentName
        });
      }
      setIsDependency(segmentQuery.data.isDependency);
    }
  }, [segmentQuery.data]);

  React.useEffect(() => {
    setCanEditSegment(powerUser || segmentQuery.data?.isEditable);
  }, [segmentQuery.data, powerUser]);

  const buildIncludeSelectedCompositionObj = React.useMemo(
    () => ({
      selectedComoposition: includeSegmentComposition,
      changeSelectedComposition: setIncludeSegmentComposition
    }),
    [includeSegmentComposition, setIncludeSegmentComposition]
  );

  const buildExcludeSelectedCompositionObj = React.useMemo(
    () => ({
      selectedComoposition: excludeSegmentComposition,
      changeSelectedComposition: setExcludeSegmentComposition
    }),
    [excludeSegmentComposition, setExcludeSegmentComposition]
  );

  return (
    <div className={styles.root}>
      <div className={styles.error}>
        {segmentQuery.isError && <SecondaryAlert type="error" message={segmentQuery.error.message} />}
        {segmentMutation.isError && <SecondaryAlert type="error" message={segmentMutation.error.message} />}
        {isDependency && <DependencyAlert type="info" message={dependencyMessage} />}
      </div>
      <CollapsibleSection title="Enter Segment Details" collapsed={detailsCollapsed} onCollapse={setDetailsCollapsed}>
        {segmentQuery.data && canEditSegment && isEditingSegment && (
          <EditSegmentDetails
            name={name}
            description={description}
            shared={shared}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onSharedChange={setShared}
          />
        )}
        {segmentQuery.data && (!canEditSegment || !isEditingSegment) && <ViewSegmentDetails segmentId={segmentId} />}
      </CollapsibleSection>
      <CollapsibleSection
        title="Summary"
        collapsed={buildCollapsed}
        onCollapse={setBuildCollapsed}
        className={styles.buildSection}
        secondaryAction={
          <React.Fragment>
            {powerUser && (
              <ExportSegment
                name={name}
                includedAttributes={includedAttributes}
                excludedAttributes={excludedAttributes}
                segmentId={segmentId}
                segmentShared={shared}
                userName={userName}
              />
            )}
          </React.Fragment>
        }
      >
        {segmentQuery.data && canEditSegment && isEditingSegment && (
          <React.Fragment>
            <BuildSegmentContext.Provider value={{ selectedAttributes: includedAttributes, dispatch: dispatch }}>
              <BuildSegmentExcludedContext.Provider
                value={{ selectedAttributes: excludedAttributes, dispatch: excludedDispatch }}
              >
                <BuildIncludeSelectedCompositionContext.Provider value={buildIncludeSelectedCompositionObj}>
                  <BuildExcludeSelectedCompositionContext.Provider value={buildExcludeSelectedCompositionObj}>
                    <SegmentResultsBuild />
                    <BuildSegment isDependency={isDependency} />
                    <BuildSegment exclude isDependency={isDependency} />
                  </BuildExcludeSelectedCompositionContext.Provider>
                </BuildIncludeSelectedCompositionContext.Provider>
              </BuildSegmentExcludedContext.Provider>
            </BuildSegmentContext.Provider>
          </React.Fragment>
        )}
        {segmentQuery.data && (!canEditSegment || !isEditingSegment) && (
          <React.Fragment>
            <SegmentResultsView />
            <ViewSegmentAttributes
              includedAttributes={segmentQuery.data.attributes.include}
              excludedAttributes={segmentQuery.data.attributes.exclude}
              includeComposition={segmentQuery.data.composition?.include}
              excludeComposition={segmentQuery.data.composition?.exclude}
            />
          </React.Fragment>
        )}
      </CollapsibleSection>
      {segmentQuery.data && canEditSegment && (
        <>
          {isEditingSegment && (
            <div className={styles.actionFooter}>
              <TertiaryButton
                component="button"
                text="Cancel"
                title="Cancel"
                handleClick={() => {
                  history.push(Paths.Segments);
                }}
              />
              <PrimaryButton
                text="Save Segment"
                title="Save Segment"
                handleClick={updateSegment}
                deactive={
                  name.length === 0 ||
                  description.length === 0 ||
                  (includedAttributes.predicates.length === 0 && excludedAttributes.predicates.length === 0) ||
                  shared === ''
                }
              />
            </div>
          )}
          {!isEditingSegment && (
            <div className={styles.editFooter}>
              <PrimaryButton text="Edit Segment" title="Edit Segment" handleClick={() => setIsEditingSegment(true)} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Segment;
